import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Header from '../components/Header';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const pollPaymentStatus = async () => {
      if (attempts >= 10) {
        setStatus('failed');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (response.data.status === 'expired') {
          setStatus('failed');
          return;
        }

        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      } catch (err) {
        console.error('Error checking payment:', err);
        setAttempts(prev => prev + 1);
        if (attempts < 5) {
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-stone-950' : 'bg-stone-50'}`}>
      <Header />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className={`rounded-3xl border p-12 ${isDark ? 'bg-stone-900/50 border-stone-800' : 'bg-white border-stone-200 shadow-lg'}`}>
            {status === 'checking' && (
              <>
                <Loader2 className={`w-20 h-20 mx-auto mb-6 animate-spin ${isDark ? 'text-amber-500' : 'text-amber-600'}`} />
                <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                  Processing Payment...
                </h1>
                <p className={isDark ? 'text-stone-400' : 'text-stone-600'}>
                  Please wait while we confirm your payment.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                  Payment Successful!
                </h1>
                <p className={`mb-8 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                  Welcome to the 7 Days to Sovereignty course. Your journey to freedom begins now.
                </p>
                <Button
                  onClick={() => navigate('/course')}
                  className="bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold px-8 py-6 text-lg"
                >
                  Start Course Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                  <XCircle className="w-12 h-12 text-red-500" />
                </div>
                <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-stone-100' : 'text-stone-800'}`}>
                  Payment Issue
                </h1>
                <p className={`mb-8 ${isDark ? 'text-stone-400' : 'text-stone-600'}`}>
                  We couldn't confirm your payment. Please try again or contact support.
                </p>
                <Button
                  onClick={() => navigate('/course/checkout')}
                  variant="outline"
                  className={isDark ? 'border-stone-700 text-stone-300' : 'border-stone-300 text-stone-600'}
                >
                  Try Again
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
